import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  Updater,
  useReactTable,
} from '@tanstack/react-table';
import classnames from 'classnames';
import { Fragment, ReactNode, useCallback, useState } from 'react';

import { SortIcon } from '../Icon';
import './Table.css';

/**
 * Props for the Table component
 */
export interface TableProps<TData> {
  /** Array of data objects to display in the table */
  data: TData[];

  /** Column definitions from @tanstack/react-table */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];

  /** Optional function to render expanded row content */
  renderExpandedRow?: (row: Row<TData>) => ReactNode;

  /** Additional CSS class name(s) for the table wrapper */
  className?: string;

  /** Enable sorting functionality. @default false */
  enableSorting?: boolean;

  /**
   * When true, data is pre-sorted by the server. Clicking headers calls
   * onSortingChange instead of sorting locally. Use with sortBy and onSortingChange.
   * @default false
   */
  manualSorting?: boolean;

  /** Controlled sort state. Use with manualSorting. */
  sortBy?: SortingState;

  /** Called when user clicks a sortable column header. Use with manualSorting. */
  onSortingChange?: (sorting: SortingState) => void;

  /** Screen reader accessible caption for 508 compliance */
  caption?: string;

  /** ARIA label for the table element */
  ariaLabel?: string;
  /** Test ID for automated testing */
  'data-testid'?: string;

  /** Optional stable row id for expandable rows; enables controlled expansion when used with expanded/onExpandedChange */
  getRowId?: (row: TData) => string;

  /** Controlled expanded state: map of row id to boolean. When provided with onExpandedChange, enables "Expand All" style controls */
  expanded?: Record<string, boolean>;

  /** Called when expanded state changes; use with expanded for controlled expansion */
  onExpandedChange?: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
}

/**
 * Table Component
 *
 * A fully accessible, sortable table component built with TanStack Table.
 * Supports expandable rows, custom styling, and comprehensive keyboard navigation.
 *
 * @template TData - Type of data objects in the table rows
 *
 * @component
 * @example
 * ```tsx
 * interface User {
 *   id: number;
 *   name: string;
 *   email: string;
 * }
 *
 * const columns: ColumnDef<User>[] = [
 *   { accessorKey: 'name', header: 'Name', enableSorting: true },
 *   { accessorKey: 'email', header: 'Email' }
 * ];
 *
 * const data: User[] = [
 *   { id: 1, name: 'John Doe', email: 'john@example.com' }
 * ];
 *
 * <Table
 *   data={data}
 *   columns={columns}
 *   enableSorting={true}
 *   caption="User directory table"
 *   ariaLabel="User directory"
 * />
 * ```
 *
 * @accessibility
 * - Uses semantic HTML table structure
 * - Includes <caption> for screen readers (when provided)
 * - aria-sort attributes on sortable columns
 * - scope="col" on all header cells
 * - Keyboard accessible sorting (click or Enter/Space on headers)
 * - Focus indicators on interactive elements
 *
 * @features
 * - Sortable columns (ascending/descending)
 * - Expandable rows with custom content
 * - Responsive design
 * - Striped rows for readability
 * - Hover effects
 * - Custom styling support
 */
function Table<TData>({
  data,
  columns,
  renderExpandedRow,
  className,
  enableSorting = false,
  manualSorting = false,
  sortBy,
  onSortingChange: onSortingChangeProp,
  caption,
  ariaLabel,
  'data-testid': dataTestId,
  getRowId,
  expanded: controlledExpanded,
  onExpandedChange,
}: TableProps<TData>) {
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [internalExpanded, setInternalExpanded] = useState({});

  const sorting = manualSorting ? sortBy ?? [] : internalSorting;
  const handleSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      if (manualSorting && onSortingChangeProp) {
        const resolved = typeof updater === 'function' ? updater(sortBy ?? []) : updater;
        onSortingChangeProp(resolved);
      } else {
        setInternalSorting(updater);
      }
    },
    [manualSorting, onSortingChangeProp, sortBy],
  );

  const expanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const setExpanded = onExpandedChange ?? setInternalExpanded;

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    columns,
    data,
    enableMultiSort: !manualSorting,
    enableSorting,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    ...(getRowId && { getRowId: getRowId as (row: TData) => string }),
    getSortedRowModel: getSortedRowModel(),
    manualSorting,
    onExpandedChange: setExpanded,
    onSortingChange: handleSortingChange,
    state: {
      expanded,
      sorting,
    },
  });

  const containerClasses = classnames('access-table', className);
  const baseTestId = dataTestId ?? 'table';

  const getSortDirection = (sortValue: false | 'asc' | 'desc'): 'ascending' | 'descending' | 'none' => {
    if (sortValue === 'asc') return 'ascending';
    if (sortValue === 'desc') return 'descending';

    return 'none';
  };

  return (
    <div
      className={containerClasses}
      data-testid={baseTestId}>
      <table
        aria-label={ariaLabel}
        className='access-table__table'
        data-testid={`${baseTestId}-table`}>
        {caption && (
          <caption
            className='ds-u-visibility--screen-reader'
            data-testid={`${baseTestId}-caption`}>
            {caption}
          </caption>
        )}
        <thead
          className='access-table__thead'
          data-testid={`${baseTestId}-head`}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className='access-table__header-row'
              data-testid={`${baseTestId}-header-row-${headerGroup.id}`}>
              {headerGroup.headers.map((header) => {
                const sortDirection = header.column.getIsSorted();

                return (
                  <th
                    key={header.id}
                    aria-sort={sortDirection ? getSortDirection(sortDirection) : undefined}
                    className={classnames('access-table__header-cell', {
                      'access-table__header-cell--sortable': header.column.getCanSort(),
                    })}
                    data-testid={`${baseTestId}-header-cell-${header.id}`}
                    scope='col'
                    style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}>
                    {header.isPlaceholder ? null : (
                      <>
                        {header.column.getCanSort() ? (
                          <button
                            className='access-table__header-content access-table__sort-button'
                            data-testid={`${baseTestId}-header-content-${header.id}`}
                            type='button'
                            onClick={header.column.getToggleSortingHandler()}>
                            <span className='ds-u-visibility--screen-reader'>Sort by</span>{' '}
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            <span className='ds-u-visibility--screen-reader'>
                              {sortDirection === 'asc'
                                ? ', currently sorted ascending'
                                : sortDirection === 'desc'
                                ? ', currently sorted descending'
                                : ', not sorted'}
                            </span>
                            <SortIcon
                              size={16}
                              sortDirection={sortDirection as 'asc' | 'desc' | null}
                            />
                          </button>
                        ) : (
                          <div
                            className='access-table__header-content'
                            data-testid={`${baseTestId}-header-content-${header.id}`}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </div>
                        )}
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody
          className='access-table__tbody'
          data-testid={`${baseTestId}-body`}>
          {table.getRowModel().rows.map((row, index) => (
            <Fragment key={row.id}>
              <tr
                className={classnames('access-table__row', {
                  'access-table__row--even': index % 2 === 1,
                  'access-table__row--expanded': row.getIsExpanded(),
                  'access-table__row--odd': index % 2 === 0,
                })}
                data-testid={`${baseTestId}-row-${row.id}`}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className='access-table__cell'
                    data-testid={`${baseTestId}-cell-${cell.id}`}
                    tabIndex={cell.column.id === 'action' || cell.column.id === 'actions' ? -1 : 0}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
              {row.getIsExpanded() && renderExpandedRow && (
                <tr
                  className={classnames('access-table__expanded-row', {
                    'access-table__expanded-row--even': index % 2 === 1,
                    'access-table__expanded-row--odd': index % 2 === 0,
                  })}
                  data-testid={`${baseTestId}-expanded-row-${row.id}`}>
                  <td
                    className='access-table__expanded-cell'
                    colSpan={row.getVisibleCells().length}
                    data-testid={`${baseTestId}-expanded-cell-${row.id}`}>
                    {renderExpandedRow(row)}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
